import { KeyboardEvent } from 'react'
import classNames from 'classnames'
import moment from 'jalali-moment'
import { motion } from 'framer-motion'
import { FiChevronLeft } from 'react-icons/fi'
import { LuMessagesSquare } from 'react-icons/lu'
import { SectionError } from 'components/states'
import { faPlain, fundName, toman } from 'utilities/helper/format'
import { localDayKey } from './messageTarget'

// The backend's real state codes (`choice.LOAN_DEMAND_STATE_*`), labelled as
// the request's own page labels them.
const STATE_LABELS: Record<ILoanDemandState, string> = {
    dmnd: 'در انتظار بررسی',
    inas: 'در حال بررسی',
    acpd: 'پذیرفته شد',
    rjcd: 'رد شد',
    cnld: 'لغو شد',
}

const STATE_TONE: Record<ILoanDemandState, string> = {
    dmnd: 'bg-warning-soft text-warning-on',
    inas: 'bg-warning-soft text-warning-on',
    acpd: 'bg-success-2 text-success-on',
    rjcd: 'bg-danger-soft text-danger-on',
    cnld: 'bg-canvas text-text-secondary',
}

export const threadPath = (thread: ILoanThread) =>
    `/fund/loan-request/detail/${thread.demand_id}?fund=${thread.moneypool_id}`

const threadTime = (timestamp: number) => {
    const now = Math.floor(Date.now() / 1000)
    const day = localDayKey(timestamp)
    const at = moment(new Date(timestamp * 1000)).locale('fa')
    if (day === localDayKey(now)) return at.format('HH:mm')
    if (day === localDayKey(now - 86_400)) return 'دیروز'
    return at.format('D MMMM')
}

const initialOf = (name: string) => (name.trim()[0] ?? '؟').toUpperCase()

interface LoanThreadsProps {
    threads: ILoanThread[] | undefined
    isLoading: boolean
    isError: boolean
    onRetry: () => void
    onOpen: (thread: ILoanThread) => void
}

/**
 * «گفت‌وگوی وام» — one row per loan request whose conversation the member is
 * in: their own requests, and the ones in funds they manage. The whole thread
 * and the reply box already live on the request's page, so a row goes there.
 *
 * Only the public comments count. A manager's notes and votes on a request are
 * for the managers, and the backend never lets them into this list.
 */
export function LoanThreads({
    threads,
    isLoading,
    isError,
    onRetry,
    onOpen,
}: LoanThreadsProps) {
    if (isLoading)
        return (
            <>
                <ThreadSkeleton />
                <ThreadSkeleton />
            </>
        )
    if (isError && !threads)
        return (
            <SectionError
                message="دریافت گفت‌وگوها انجام نشد"
                onRetry={onRetry}
            />
        )
    if (!threads?.length) return <NoThreads />

    return (
        <>
            <p className="px-1 text-[12px] leading-6 text-text-secondary md:text-[13px]">
                گفت‌وگوی شما و مدیر صندوق روی درخواست‌های وام؛ برای پاسخ دادن
                روی هر کدام بزنید.
            </p>
            {threads.map((thread, index) => (
                <ThreadCard
                    key={thread.demand_id}
                    thread={thread}
                    index={index}
                    onOpen={onOpen}
                />
            ))}
        </>
    )
}

function ThreadCard({
    thread,
    index,
    onOpen,
}: {
    thread: ILoanThread
    index: number
    onOpen: (thread: ILoanThread) => void
}) {
    const who = thread.is_mine ? 'درخواست شما' : thread.requester_name
    const speaker = thread.last.by_me ? 'شما' : thread.last.actor_name
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen(thread)
        }
    }

    return (
        <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.32,
                ease: [0.16, 1, 0.3, 1],
                delay: Math.min(index, 8) * 0.04,
            }}
            role="link"
            tabIndex={0}
            onClick={() => onOpen(thread)}
            onKeyDown={onKeyDown}
            aria-label={`گفت‌وگوی وام ${fundName(thread.fund_name)}، ${who}`}
            className={classNames(
                'relative cursor-pointer overflow-hidden rounded-card border bg-white p-3.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-green-1/50 active:bg-sunken md:p-4',
                thread.awaiting_reply
                    ? 'border-warning-1/50 shadow-badge'
                    : 'border-divider'
            )}
        >
            {thread.awaiting_reply && (
                <span
                    aria-hidden
                    className="absolute inset-y-3 start-0 w-[3px] rounded-e-full bg-warning-1"
                />
            )}

            <div className="flex items-start gap-2.5 md:gap-3">
                <span
                    aria-hidden
                    className={classNames(
                        'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold md:h-10 md:w-10 md:text-sm',
                        thread.is_mine
                            ? 'bg-brand-soft text-brand-deep'
                            : 'bg-blue-6 text-blue-1'
                    )}
                >
                    {thread.is_mine ? (
                        <LuMessagesSquare className="h-[18px] w-[18px]" />
                    ) : (
                        initialOf(thread.requester_name)
                    )}
                </span>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="truncate text-[13px] font-bold text-heading md:text-sm">
                            {fundName(thread.fund_name)}
                        </h3>
                        <span className="shrink-0 text-[11px] text-text-muted md:text-xs">
                            {threadTime(thread.last.timestamp)}
                        </span>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-text-secondary md:text-xs">
                        {who} · {toman(thread.amount)}
                    </p>
                </div>
            </div>

            <p className="mt-2.5 line-clamp-2 break-words rounded-lg bg-canvas px-3 py-2 text-[13px] leading-6 text-text-body md:text-sm md:leading-7">
                <span
                    className={classNames(
                        'font-bold',
                        thread.last.by_me ? 'text-brand-deep' : 'text-heading'
                    )}
                >
                    {speaker}:{' '}
                </span>
                {thread.last.note}
            </p>

            <div className="mt-2.5 flex items-center gap-1.5">
                <span
                    className={classNames(
                        'rounded-full px-2 py-0.5 text-[11px] font-bold md:text-xs',
                        STATE_TONE[thread.state] ?? STATE_TONE.cnld
                    )}
                >
                    {STATE_LABELS[thread.state] ?? thread.state}
                </span>
                {thread.awaiting_reply && (
                    <span className="flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[11px] font-bold text-warning-on md:text-xs">
                        <span className="relative block h-2 w-2">
                            <span className="absolute inset-0 animate-ping rounded-[2px] bg-warning-1 opacity-50 [animation-duration:1.8s]" />
                            <span className="absolute inset-0 rounded-[2px] bg-warning-1" />
                        </span>
                        منتظر پاسخ شما
                    </span>
                )}
                <span className="ms-auto flex items-center gap-0.5 text-[11px] font-bold text-green-1 md:text-xs">
                    {faPlain.format(thread.count)} پیام
                    <FiChevronLeft className="h-4 w-4" aria-hidden />
                </span>
            </div>
        </motion.article>
    )
}

function ThreadSkeleton() {
    return (
        <div className="animate-pulse rounded-card border border-divider bg-white p-3.5 md:p-4">
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 shrink-0 rounded-full bg-divider md:h-10 md:w-10" />
                <div className="flex-1 pt-1">
                    <div className="h-3 w-28 rounded bg-divider" />
                    <div className="mt-2 h-2.5 w-40 rounded bg-divider" />
                </div>
            </div>
            <div className="mt-4 h-10 w-full rounded-lg bg-divider" />
        </div>
    )
}

function NoThreads() {
    return (
        <div className="flex flex-col items-center px-8 pt-14 text-center animate-fade-in">
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-blue-6 md:h-24 md:w-24">
                <span className="absolute inset-3 rounded-full bg-white/70" />
                <LuMessagesSquare className="relative h-9 w-9 text-blue-1 md:h-10 md:w-10" />
            </div>
            <p className="mt-5 text-[15px] font-bold text-heading md:text-base">
                هنوز گفت‌وگویی دربارهٔ وام ندارید
            </p>
            <p className="mt-2 text-[13px] leading-6 text-text-secondary md:text-sm md:leading-7">
                وقتی روی درخواست وام شما، یا درخواستی در صندوقی که مدیرش هستید،
                پیامی گذاشته شود، اینجا می‌آید.
            </p>
        </div>
    )
}
